import { Component, Input, OnInit } from '@angular/core';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { MatLegacyTableDataSource as MatTableDataSource } from '@angular/material/legacy-table';
import { LegacyPageEvent as PageEvent } from '@angular/material/legacy-paginator';
import { BUBBLE_COLOR_ENUM, bubbleColor, FeiColumn } from 'src/app/core/base/interfaces/fei-column.interface';
import { DataTableBase } from 'src/app/core/base/components/data-table-base/data-table-base';
import { ContractResumeCreateUpdateDeleteComponent } from '../contract-resume-create-update-delete/contract-resume-create-update-delete.component';
import { EstablishmentService } from '../../../establishment/establishment.service';
import { forkJoin, switchMap, tap } from 'rxjs';
import { Sort } from '@angular/material/sort';
import { ContractService } from '../../contract.service';
import { Router } from '@angular/router';
import { ContractModel } from '../../../../../../core/models/contract.model';

@Component({
  selector: 'vex-engagement-select',
  templateUrl: './engagement-select.component.html',
  styleUrls: ['./engagement-select.component.scss'],
})
export class EngagementSelectComponent extends DataTableBase<ContractModel> implements OnInit {
  @Input() supportEstablishment: any;
  @Input() selectedEngagements: ContractModel[] = [];
  @Input() currentEngagement: ContractModel;
  feisColumns: Array<FeiColumn> = [
    { label: '', column: 'selection' },
    { label: 'Objet engagement', column: 'object', propertyDisplay: 'object' },
    { label: 'Fournisseur', column: 'supplier', propertyDisplay: 'supplier.label' },
    {
      label: 'Id Engagement',
      column: 'technicalIdentifier',
      propertyDisplay: 'technicalIdentifier',
    },
    {
      label: 'Début de la validité',
      column: 'startValidityDate',
      propertyDisplay: 'startValidityDate',
      type: 'date',
    },
    {
      label: 'Fin de validité',
      column: 'endValidityDate',
      propertyDisplay: 'endValidityDate',
      type: 'date',
      statusClassFunc: this.displayEndValidityDateStatus.bind(this),
    },
  ];

  displayedColumns: string[] = this.feisColumns.map(c => c.column);

  inPerimeterDataSource = new MatTableDataSource<ContractModel>();
  outPerimeterDataSource = new MatTableDataSource<ContractModel>();

  selectedContracts: ContractModel[] = [];

  selectedIn: ContractModel[] = [];
  selectedOut: ContractModel[] = [];
  countIn = 0;
  countOut = 0;
  pageIndexIn = 1;
  pageIndexOut = 1;
  pageSize = 10;
  sortField = 'contract.technicalIdentifier';
  sortOrder = 'desc';

  legendData = [{ description: 'Échéances proches', color: '#FFC0CB' }];

  loading = false;
  selectedCount = 0;

  constructor(
    private contractService: ContractService,
    public establishmentService: EstablishmentService,
    private dialog: MatDialog,
    private router: Router,
  ) {
    super();
    this.service = contractService;
  }

  ngOnInit(): void {
    this.initData();
  }

  initData() {
    this.loading = true;
    if (this.selectedEngagements?.length) {
      this.selectedContracts = [...this.selectedEngagements];
      this.updateSelectedCount();
    }

    this.establishmentService
      .findOneById(this.supportEstablishment._id)
      .pipe(
        tap(() => {}),
        switchMap(() => {
          return forkJoin({
            inPerimeter: this.fetchContracts(true),
            outPerimeter: this.fetchContracts(false),
          });
        }),
      )
      .subscribe({
        next: ({ inPerimeter, outPerimeter }: any) => {
          this.countIn = inPerimeter.totalData;
          this.countOut = outPerimeter.totalData;
          this.inPerimeterDataSource.data = inPerimeter.data;
          this.outPerimeterDataSource.data = outPerimeter.data;
          this.restoreSelection();
          this.loading = false;
        },
        error: () => {
          this.loading = false;
        },
      });
  }

  // Override sort to refresh both tabs with the same sort
  sortEvent(event: Sort): void {
    const property = this.feisColumns.find(c => c.column === event.active)?.propertyDisplay || event.active;
    this.sortField = property;
    this.sortOrder = event.direction;
    this.loading = true;
    forkJoin({
      inPerimeter: this.fetchContracts(true),
      outPerimeter: this.fetchContracts(false),
    }).subscribe({
      next: ({ inPerimeter, outPerimeter }: any) => {
        this.countIn = inPerimeter.totalData;
        this.countOut = outPerimeter.totalData;
        this.inPerimeterDataSource.data = inPerimeter.data as unknown as ContractModel[];
        this.outPerimeterDataSource.data = outPerimeter.data as unknown as ContractModel[];
        this.restoreSelection();
        this.loading = false;
      },
      error: () => {
        this.loading = false;
      },
    });
  }

  private resolveGenericEngagementId(): string | null {
    return this.currentEngagement?.genericEngagementId || this.currentEngagement?.genericEngagement?._id || null;
  }

  private buildEngagementFilters(isIn: boolean) {
    const genericEngagementId = this.resolveGenericEngagementId();
    const base: any = {
      isContractValid: true,
      supportEstablishmentId: [this.supportEstablishment._id],
      isInternal: false, // exclude internal contracts
    };
    if (genericEngagementId) {
      base.genericEngagementId = genericEngagementId;
    }
    if (isIn) {
      base.establishments = [this.supportEstablishment._id, this.supportEstablishment?.mainEstablishmentId].filter(
        e => !!e,
      );
    } else {
      base.onlyCustomers = true;
    }
    return base;
  }

  private fetchContracts(isIn: boolean) {
    const pageIndex = isIn ? this.pageIndexIn : this.pageIndexOut;
    const filters = this.buildEngagementFilters(isIn);
    return this.contractService.findAll(pageIndex, this.pageSize, this.sortField, this.sortOrder, this.search, filters);
  }

  selectRow(rows: ContractModel[], isIn: boolean) {
    const pageIds = (isIn ? this.inPerimeterDataSource.data : this.outPerimeterDataSource.data).map(c => c._id);
    this.selectedContracts = this.selectedContracts.filter(c => !pageIds.includes(c._id));
    this.selectedContracts = [...this.selectedContracts, ...rows];

    if (isIn) {
      this.selectedIn = rows;
    } else {
      this.selectedOut = rows;
    }

    this.updateSelectedCount();
  }

  hasSelection(): boolean {
    return this.selectedContracts.length > 0;
  }

  displayEndValidityDateStatus(element: ContractModel): bubbleColor {
    return this.compareDateWithCurrent(element.endValidityDate, 3) ? BUBBLE_COLOR_ENUM.RED : BUBBLE_COLOR_ENUM.EMPTY;
  }

  compareDateWithCurrent(inputDate: Date | string, months: number, days: number = 0): boolean {
    if (!inputDate) {
      return false;
    }

    const date = typeof inputDate === 'string' ? new Date(inputDate) : inputDate;
    const currentDate = new Date();

    if (isNaN(date.getTime()) || date.getTime() < currentDate.getTime()) {
      return false;
    }

    const yearDiff = currentDate.getFullYear() - date.getFullYear();
    const monthDiff = currentDate.getMonth() - date.getMonth();
    const totalMonthDiff = yearDiff * 12 + monthDiff;

    const dayDiff = currentDate.getDate() - date.getDate();

    if (Math.abs(totalMonthDiff) > months) {
      return false;
    }

    return !(Math.abs(totalMonthDiff) === months && dayDiff > days);
  }

  restoreSelection() {
    if (!this.selectedContracts?.length) {
      return;
    }
    const ids = this.selectedContracts.map(c => c._id);
    this.selectedIn = this.inPerimeterDataSource.data.filter(c => ids.includes(c._id));
    this.selectedOut = this.outPerimeterDataSource.data.filter(c => ids.includes(c._id));
    this.updateSelectedCount();
  }

  onPageEventIn(event: PageEvent) {
    this.pageIndexIn = event.pageIndex + 1;
    this.pageSize = event.pageSize;
    this.loading = true;
    this.fetchContracts(true).subscribe({
      next: res => {
        this.countIn = res.totalData;
        this.inPerimeterDataSource.data = res.data as unknown as ContractModel[];
        this.restoreSelection();
        this.loading = false;
      },
      error: () => {
        this.loading = false;
      },
    });
  }

  onPageEventOut(event: PageEvent) {
    this.pageIndexOut = event.pageIndex + 1;
    this.pageSize = event.pageSize;
    this.loading = true;
    this.fetchContracts(false).subscribe({
      next: res => {
        this.countOut = res.totalData;
        this.outPerimeterDataSource.data = res.data as unknown as ContractModel[];
        this.restoreSelection();
        this.loading = false;
      },
      error: () => {
        this.loading = false;
      },
    });
  }

  async openEngagement(contract: ContractModel) {
    if (!contract?._id) {
      console.error('Contract ID is missing', contract);
      return;
    }
    try {
      this.dialog.closeAll();
    } catch {}
    await this.router.navigate(['/management/engagement', contract._id]);
  }

  /** Sélectionne tous les résultats (toutes les pages) dans le périmètre */
  selectAllInPerimeter() {
    if (this.countIn <= 0) {
      return;
    }
    this.loading = true;
    const filters = this.buildEngagementFilters(true);
    this.contractService.findAll(1, this.countIn, this.sortField, this.sortOrder, this.search, filters).subscribe({
      next: res => {
        this.selectedContracts = res.data as unknown as ContractModel[];
        this.restoreSelection();
        this.updateSelectedCount();
        this.loading = false;
      },
      error: () => {
        this.loading = false;
      },
    });
  }

  clearAllSelections() {
    this.selectedContracts = [];
    this.selectedIn = [];
    this.selectedOut = [];
    this.updateSelectedCount();
  }

  private updateSelectedCount() {
    this.selectedCount = this.selectedContracts?.length ?? 0;
  }
}
